// People & replies (prototype G): full-width list → split detail. j/k/↑/↓ move, Enter opens, Esc closes,
// "/" searches, ?v= view, ?s= chip, ?p= person deep link.
import { MagnifyingGlass, Sparkle, UsersThree, X } from "@phosphor-icons/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Ic, useTick } from "@/components/bits";
import { fmt } from "@/lib/format";
import {
  chipFilter,
  hasReplied,
  matchesSearch,
  needsAttention,
  VIEW_CHIPS,
  type View,
  viewList,
} from "@/model/overview";
import type { Person } from "@/model/people";
import { plannedBatch } from "@/model/runs";
import { type PStatus, STATUS_LABEL } from "@/model/status";
import type { PeopleSearch } from "@/router";
import { useApp } from "@/shell/appData";
import { Detail } from "./Detail";
import { PersonRow } from "./PersonRow";

const VIEWS: { k: View; long: string; short: string }[] = [
  { k: "all", long: "All people", short: "All" },
  { k: "attention", long: "Needs attention", short: "Attention" },
  { k: "replied", long: "Replied", short: "Replied" },
];

const CHIP_LABEL: Record<string, string> = {
  waiting: "Waiting for you",
  open: "Can reply",
  closed: "Window closed",
  onway: "On the way",
  got: "Got the confirmation",
  fix: "Needs a fix",
  messaged: "Messaged",
  unread: "Delivered, not read",
};

function chipLabel(k: string, view: View): string {
  if (k === "any") return view === "all" ? "Everyone" : "All";
  return CHIP_LABEL[k] ?? STATUS_LABEL[k as PStatus] ?? k;
}

const reducedMotion = () => {
  try {
    return matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};
const isWide = () => window.innerWidth >= 768;

export function People() {
  const search = useSearch({ from: "/people" });
  const navigate = useNavigate({ from: "/people" });
  const app = useApp();
  const now = useTick(30_000);
  const view: View = search.v ?? "all";
  const chip = search.s ?? "any";
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(60);
  const [foc, setFoc] = useState<string | null>(null);
  const [dir, setDir] = useState(0);

  const sel = useMemo(
    () => (search.p ? (app.people.find((p) => p.id === search.p) ?? null) : null),
    [search.p, app.people],
  );
  const base = useMemo(() => viewList(app.people, view, now), [app.people, view, now]);
  const list = useMemo(
    () => base.filter(chipFilter(chip, now)).filter((p) => matchesSearch(p, q)),
    [base, chip, now, q],
  );

  const setSearch = useCallback(
    (patch: Partial<PeopleSearch>) =>
      navigate({ search: (prev: PeopleSearch) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const select = useCallback(
    (id: string) => {
      const a = list.findIndex((p) => p.id === search.p);
      const b = list.findIndex((p) => p.id === id);
      setDir(a < 0 || b < 0 ? 2 : b > a ? 1 : -1);
      setFoc(id);
      setSearch({ p: id });
    },
    [list, search.p, setSearch],
  );
  const close = useCallback(() => {
    if (!search.p) return;
    setFoc(search.p);
    setSearch({ p: undefined });
  }, [search.p, setSearch]);

  // keep the selected row rendered
  useEffect(() => {
    if (!sel) return;
    const k = list.findIndex((p) => p.id === sel.id);
    if (k >= limit) setLimit(k + 20);
  }, [sel, list, limit]);

  // ---------------------------------------------------------------- split open/close animation
  const ws = useRef<HTMLDivElement>(null);
  const [detOpen, setDetOpen] = useState(!!sel);
  const [anim, setAnim] = useState(false);
  const [shown, setShown] = useState<Person | null>(sel);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (sel) setShown(sel);
  }, [sel]);
  useLayoutEffect(() => {
    const open = !!sel;
    if (open === detOpen) return;
    const el = ws.current;
    if (!el || !isWide() || reducedMotion()) {
      setDetOpen(open);
      if (!open) setShown(null);
      return;
    }
    const lw = Number.parseFloat(getComputedStyle(el).getPropertyValue("--lw")) || 360;
    el.style.setProperty("--detw", `${Math.max(320, el.clientWidth - lw)}px`);
    setAnim(true);
    setDetOpen(open);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setAnim(false);
      el.style.removeProperty("--detw");
      if (!open) setShown(null);
    }, 440);
  }, [sel, detOpen]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // ---------------------------------------------------------------- selection highlight + segment pill
  const plist = useRef<HTMLDivElement>(null);
  const hl = useRef<HTMLDivElement>(null);
  const hlPlaced = useRef(false);
  const placeHl = useCallback(
    (instant: boolean) => {
      const h = hl.current;
      const id = search.p ?? foc;
      const row = id ? plist.current?.querySelector<HTMLElement>(`.pr[data-p="${CSS.escape(id)}"]`) : null;
      if (!h) return;
      h.classList.toggle("kf", !search.p);
      if (!row) {
        h.style.opacity = "0";
        hlPlaced.current = false;
        return;
      }
      const noAnim = instant || !hlPlaced.current;
      if (noAnim) h.classList.add("noanim");
      h.style.opacity = "1";
      h.style.height = `${row.offsetHeight}px`;
      h.style.transform = `translateY(${row.offsetTop}px)`;
      if (noAnim) {
        void h.offsetWidth;
        h.classList.remove("noanim");
      }
      hlPlaced.current = true;
    },
    [search.p, foc],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-place when the rows or the split change
  useLayoutEffect(() => placeHl(false), [placeHl, list, limit, detOpen]);
  useEffect(() => {
    if (!plist.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => placeHl(true));
    ro.observe(plist.current);
    return () => ro.disconnect();
  }, [placeHl]);

  const seg = useRef<HTMLDivElement>(null);
  const segReady = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: move the pill when the view or the layout changes
  useLayoutEffect(() => {
    const on = seg.current?.querySelector<HTMLElement>("button.on");
    const pill = seg.current?.querySelector<HTMLElement>(".seg-pill");
    if (!on || !pill) return;
    if (!segReady.current) pill.style.transition = "none";
    pill.style.width = `${on.offsetWidth}px`;
    pill.style.transform = `translateX(${on.offsetLeft}px)`;
    if (!segReady.current) {
      void pill.offsetWidth;
      pill.style.transition = "";
      segReady.current = true;
    }
  }, [view, detOpen, anim]);

  // ---------------------------------------------------------------- keyboard
  const searchBox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const move = (d: number) => {
      if (!list.length) return;
      const cur = search.p ?? foc;
      let k = list.findIndex((p) => p.id === cur);
      k = k < 0 ? 0 : Math.max(0, Math.min(list.length - 1, k + d));
      if (k >= limit) setLimit(k + 20);
      const id = list[k]!.id;
      if (search.p) select(id);
      else setFoc(id);
      requestAnimationFrame(() => {
        const el = plist.current?.querySelector<HTMLElement>(`.pr[data-p="${CSS.escape(id)}"]`);
        // move DOM focus too, so Enter on the focused row opens the row j/k landed on
        el?.focus({ preventScroll: true });
        el?.scrollIntoView({ block: "nearest", behavior: reducedMotion() ? "auto" : "smooth" });
      });
    };
    const onKey = (e: KeyboardEvent) => {
      if (document.querySelector(".cmdk") || document.querySelector(".pop.rdx")) return;
      const ae = document.activeElement as HTMLElement | null;
      const typing = ae?.tagName === "INPUT" || ae?.tagName === "TEXTAREA";
      if (e.key === "Escape") {
        if (search.p) {
          if (typing) ae?.blur();
          close();
        }
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        move(1);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        move(-1);
      } else if (e.key === "Enter" && !search.p && foc) {
        if (ae && ae !== document.body && ae.closest("button,a")) return;
        e.preventDefault();
        select(foc);
      } else if (e.key === "/") {
        e.preventDefault();
        searchBox.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list, search.p, foc, limit, select, close]);

  // ---------------------------------------------------------------- render
  const viewCount: Record<View, number> = {
    all: app.people.length,
    attention: app.people.filter(needsAttention).length,
    replied: app.people.filter(hasReplied).length,
  };
  const chips = VIEW_CHIPS[view];
  const extra = !chips.includes(chip) ? chip : null;
  const cnt = (k: string) => fmt(base.filter(chipFilter(k, now)).length);
  const planned = (p: Person) => plannedBatch(p.id, app.next, app.runs.length, app.cap);
  const regs = list.reduce((n, p) => n + p.regs.length, 0);

  return (
    <div ref={ws} className={`ws${detOpen ? " det-open" : ""}${anim ? " anim" : ""}`} id="ws" data-testid="people-ws">
      <section className="ws-list" aria-label="People list">
        <div className="ws-top">
          <h1>People &amp; replies</h1>
          <div className="seg" ref={seg} role="tablist" aria-label="View">
            <span className="seg-pill" />
            {VIEWS.map((v) => (
              <button
                key={v.k}
                type="button"
                role="tab"
                aria-selected={view === v.k}
                className={view === v.k ? "on" : ""}
                onClick={() => view !== v.k && setSearch({ v: v.k === "all" ? undefined : v.k, s: undefined })}
              >
                <span className="sl">{v.long}</span>
                <span className="ss">{v.short}</span>
                <span className={`c${v.k === "attention" && viewCount[v.k] ? " hot" : ""}`}>{fmt(viewCount[v.k])}</span>
              </button>
            ))}
          </div>
          <div className="search">
            <Ic i={MagnifyingGlass} />
            <input
              ref={searchBox}
              className="input"
              placeholder="Search name, reg ID or last 4 digits"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setLimit(60);
              }}
              aria-label="Search people"
              autoComplete="off"
            />
          </div>
          <div className="chips" data-testid="chips">
            {extra ? (
              <button type="button" className="chip on" onClick={() => setSearch({ s: undefined })}>
                {chipLabel(extra, view)} <span className="c">{cnt(extra)}</span>
                <Ic i={X} />
              </button>
            ) : null}
            {chips.map((k) => (
              <button
                key={k}
                type="button"
                className={`chip${chip === k ? " on" : ""}`}
                onClick={() => {
                  setLimit(60);
                  setSearch({ s: chip === k || k === "any" ? undefined : k });
                }}
              >
                {k === "family" ? (
                  <>
                    <Ic i={UsersThree} />
                    Families
                  </>
                ) : (
                  chipLabel(k, view)
                )}{" "}
                <span className="c">{cnt(k)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="plist" ref={plist} data-testid="plist">
          <div className="sel-hl" ref={hl} />
          {list.length ? (
            <div className="pl-h" aria-hidden="true">
              <span className="c1">Person</span>
              <span className="w-ph">Phone</span>
              <span className="w-reg">Reg ID</span>
              <span className="w-b">Batch</span>
              <span className="w-td">What to do</span>
              <span className="w-tm">Last</span>
            </div>
          ) : null}
          {list.slice(0, limit).map((p, k) => (
            <PersonRow
              key={p.id}
              p={p}
              on={search.p === p.id}
              view={view}
              now={now}
              planned={planned(p)}
              rows={app.runRows}
              style={{ "--d": `${Math.min(k, 14) * 14}ms` } as CSSProperties}
              onClick={() => (search.p === p.id && isWide() ? undefined : select(p.id))}
            />
          ))}
          {!list.length ? (
            <div className="empty" style={{ boxShadow: "none", background: "transparent", padding: "44px 16px" }}>
              <div className="ei">
                <Ic i={view === "attention" && !q ? Sparkle : MagnifyingGlass} />
              </div>
              <h3>{view === "attention" && !q ? "Nothing needs you" : "Nobody matches"}</h3>
              <p>
                {view === "attention" && !q
                  ? "No replies waiting and no numbers to fix."
                  : "Try another filter or search."}
              </p>
            </div>
          ) : (
            <div className="list-foot">
              <span>
                {fmt(Math.min(limit, list.length))} of {fmt(list.length)} · {fmt(regs)} registrations
              </span>
              {list.length > limit ? (
                <button type="button" className="btn line xs" onClick={() => setLimit(limit + 80)}>
                  Show more
                </button>
              ) : null}
            </div>
          )}
        </div>
      </section>
      <section className="ws-det" aria-label="Person details" data-testid="detail">
        {shown ? (
          <Detail
            key={shown.id}
            p={app.people.find((x) => x.id === shown.id) ?? shown}
            dir={dir}
            now={now}
            planned={planned(shown)}
            onClose={close}
            onNav={(d) => {
              const k = list.findIndex((x) => x.id === shown.id);
              const t = list[Math.max(0, Math.min(list.length - 1, (k < 0 ? 0 : k) + d))];
              if (t) select(t.id);
            }}
          />
        ) : null}
      </section>
    </div>
  );
}
